import {
  studentAssessmentCompletePath,
  studentAssessmentLivePath,
  studentAssessmentPath,
  studentAssessmentResultsPath,
} from '../constants/studentAssessments';
import { isAssessmentClosedForStudents } from './assessmentLifecyclePresentation';

/** Primary CTA target from list/dashboard row — presentation routing only. */
export function studentAssessmentAction(item, liveView) {
  const attemptState = liveView?.state || item?.attemptState;
  const status = item?.status;

  if (attemptState === 'active') {
    return { path: studentAssessmentLivePath(item.assessmentId), label: 'Continue' };
  }

  if (['submitted', 'expired', 'finalised'].includes(attemptState)) {
    if (['finalised', 'archived'].includes(status)) {
      return { path: studentAssessmentResultsPath(item.assessmentId), label: 'View results' };
    }
    return { path: studentAssessmentCompletePath(item.assessmentId), label: 'View summary' };
  }

  return { path: studentAssessmentPath(item.assessmentId), label: 'Open' };
}

/** Short attempt-state label for chips and captions. */
export function studentAssessmentAttemptLabel(attemptState, liveView, assessmentStatus) {
  const state = liveView?.state || attemptState;
  const status = liveView?.assessment?.status || assessmentStatus;
  if (isAssessmentClosedForStudents(status) && state === 'expired') return 'Closed';
  if (state === 'active') return 'In progress';
  if (state === 'submitted') return 'Submitted';
  if (state === 'expired') return 'Time expired';
  if (state === 'finalised') return 'Completed';
  return null;
}

const SHORT_DATE = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };

/** Window hint for dashboard/list rows. */
export function formatAssessmentWindowHint(item, liveView) {
  const attemptState = liveView?.state || item?.attemptState;
  const assessmentStatus = liveView?.assessment?.status || item?.status;
  const now = Date.now();
  const opensAt = item?.window?.opensAt ? new Date(item.window.opensAt).getTime() : null;
  const closesAt = item?.window?.closesAt ? new Date(item.window.closesAt).getTime() : null;

  if (isAssessmentClosedForStudents(assessmentStatus)) {
    return 'Assessment closed by faculty';
  }
  if (attemptState === 'active') {
    return 'In progress — pick up where you left off';
  }
  if (opensAt && now < opensAt) {
    return `Opens ${new Date(opensAt).toLocaleString(undefined, SHORT_DATE)}`;
  }
  if (closesAt && now <= closesAt) {
    return `Open now · closes ${new Date(closesAt).toLocaleString(undefined, SHORT_DATE)}`;
  }
  if (item?.preflightAvailable) {
    return 'Pre-flight available before the window opens';
  }
  return 'Window not set';
}

function windowOpensMs(item) {
  const at = item?.window?.opensAt;
  if (at == null) return 0;
  const ms = new Date(at).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/** Dashboard sort: in-progress first, then open window, then upcoming. */
export function sortDashboardAssessments(items, liveViewByAssessmentId = {}) {
  const rank = (item) => {
    const liveView = liveViewByAssessmentId[item?.assessmentId];
    if (isAssessmentClosedForStudents(liveView?.assessment?.status || item?.status)) return 3;
    const state = liveView?.state || item?.attemptState;
    if (state === 'active') return 0;
    if (item?.canStart) return 1;
    return 2;
  };

  return [...items].sort((a, b) => {
    const byRank = rank(a) - rank(b);
    if (byRank !== 0) return byRank;
    return windowOpensMs(a) - windowOpensMs(b);
  });
}
